import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/form-controls";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";
import { usePermission } from "@/hooks/use-permission";
import {
  fetchWebhookEndpoints,
  createWebhook,
  deleteWebhook,
  testWebhook,
  fetchWebhookDeliveries,
} from "@/lib/api";
import type { WebhookEndpoint, WebhookDelivery } from "@/types";

/* DEV-02 — Webhooks (ALL-4739, wired to the real API).
   Backend: WebhookController / WebhookRegistryService in
   alloy-api-gateway-service (scope WEBHOOKS_MANAGE + permission
   webhook.provider.ingest; create/delete/test require step-up, disabled
   on dev via the sandbox compose's ALLOY_STEP_UP_ENABLED=false).

   Deliberately NOT implemented, because no backend route exists for
   them — see product-design/specs/08-developer-platform/developer-webhooks-spec.md
   "Explicitly out of scope":
     - editing an endpoint's URL/events (no PUT/PATCH route)
     - an enable/disable toggle (only delete, which is permanent)
     - retrying one specific historical delivery (POST .../test only
       queues a NEW test delivery)
     - a delivery request/response payload viewer (the API returns no
       payload body, host, or latency)
   Faking any of the above would be exactly the "fabricated UI counts as
   mock data" failure mode the page pipeline calls out (G6 step 0). */

const WEBHOOK_EVENT_TYPES = [
  "transfer.status_changed",
  "transfer.submitted",
  "transfer.completed",
  "transfer.failed",
  "approval.required",
  "approval.resolved",
  "wallet.created",
  "diagnostics.timeline_available",
] as const;

const DEFAULT_EVENT_SELECTION = ["transfer.status_changed", "approval.required"];

type LoadState = "loading" | "ready" | "empty" | "denied" | "error";

function formatSecretRef(secretRef: string | null): string {
  if (!secretRef) return "not issued";
  return secretRef;
}

function DeliveryStatusBadge({ status }: { status: string }) {
  const normalized = status.toUpperCase();
  if (normalized === "QUEUED" || normalized === "RETRYING") {
    return <Badge variant="pending" size="sm">{status}</Badge>;
  }
  if (normalized === "DELIVERED" || normalized === "SUCCEEDED") {
    return <Badge variant="success" size="sm">{status}</Badge>;
  }
  if (normalized === "FAILED" || normalized === "EXHAUSTED") {
    return <Badge variant="danger" size="sm">{status}</Badge>;
  }
  return <Badge variant="neutral" size="sm">{status}</Badge>;
}

export function WebhooksPage() {
  const canManage = usePermission("webhook:manage");

  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebhookEndpoint | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<Record<string, boolean>>(
    Object.fromEntries(WEBHOOK_EVENT_TYPES.map((e) => [e, DEFAULT_EVENT_SELECTION.includes(e)])),
  );

  const [selectedForDeliveries, setSelectedForDeliveries] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadState("loading");
    setLoadError(null);
    const result = await fetchWebhookEndpoints();
    if (result.denied) {
      setLoadState("denied");
      return;
    }
    if (result.error) {
      setLoadError(result.error);
      setLoadState("error");
      return;
    }
    setEndpoints(result.endpoints);
    setLoadState(result.endpoints.length === 0 ? "empty" : "ready");
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadDeliveries = useCallback(async (webhookId: string) => {
    setSelectedForDeliveries(webhookId);
    setDeliveriesLoading(true);
    setDeliveriesError(null);
    const result = await fetchWebhookDeliveries(webhookId);
    if (result.error) {
      setDeliveriesError(result.error);
      setDeliveries([]);
    } else {
      setDeliveries(result.deliveries);
    }
    setDeliveriesLoading(false);
  }, []);

  function openAdd() {
    setUrl("");
    setEvents(Object.fromEntries(WEBHOOK_EVENT_TYPES.map((e) => [e, DEFAULT_EVENT_SELECTION.includes(e)])));
    setAddOpen(true);
  }

  async function saveEndpoint() {
    if (!canManage) return;
    const trimmed = url.trim();
    if (!trimmed) {
      toast({ variant: "error", title: "Enter an endpoint URL." });
      return;
    }
    const selectedEvents = WEBHOOK_EVENT_TYPES.filter((e) => events[e]);
    if (selectedEvents.length === 0) {
      toast({ variant: "error", title: "Select at least one event." });
      return;
    }
    setSaving(true);
    const result = await createWebhook({ url: trimmed, eventTypes: selectedEvents });
    setSaving(false);
    if (!result.ok) {
      toast({ variant: "error", title: "Could not add endpoint.", description: result.error });
      return;
    }
    toast({
      variant: "success",
      title: "Endpoint added.",
      description: "Alloy manages this endpoint's signing secret; it is never shown here.",
    });
    setAddOpen(false);
    await load();
  }

  async function doDelete() {
    if (!canManage || !deleteTarget) return;
    setDeleting(true);
    const result = await deleteWebhook(deleteTarget.id);
    setDeleting(false);
    if (!result.ok) {
      toast({ variant: "error", title: "Could not delete endpoint.", description: result.error });
      return;
    }
    toast({ variant: "success", title: "Endpoint deleted." });
    if (selectedForDeliveries === deleteTarget.id) setSelectedForDeliveries(null);
    setDeleteTarget(null);
    await load();
  }

  async function sendTest(endpoint: WebhookEndpoint) {
    if (!canManage) return;
    setTestingId(endpoint.id);
    const result = await testWebhook(endpoint.id);
    setTestingId(null);
    if (!result.ok) {
      toast({ variant: "error", title: "Test delivery failed to queue.", description: result.error });
      return;
    }
    // Honest result: the API queues a delivery asynchronously. It does not
    // return a synchronous 200 OK from the destination, so the UI must not
    // claim one.
    toast({
      variant: "success",
      title: `Test delivery ${result.status?.toLowerCase() ?? "queued"}.`,
      description: result.evidenceRef ? `Evidence: ${result.evidenceRef}` : undefined,
    });
    if (selectedForDeliveries === endpoint.id) {
      await loadDeliveries(endpoint.id);
    }
  }

  if (loadState === "denied") {
    return (
      <div>
        <Link to="/developer" className="mb-4 inline-flex min-h-[36px] items-center gap-1.5 text-sm text-muted hover:text-primary">← Back to Developers</Link>
        <Card>
          <CardBody>
            <div className="flex items-start gap-3 py-4 text-center">
              <div className="mx-auto flex max-w-[420px] flex-col items-center gap-3">
                <div className="grid size-10 place-items-center rounded-full bg-danger-soft">
                  <ShieldAlert className="size-5 text-danger" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-ink">You don't have access to webhooks</h2>
                  <p className="mt-1 text-sm text-muted">
                    Managing webhook endpoints requires the <code>webhooks:manage</code> scope. Ask an
                    organization admin to grant it, then reload this page.
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Link to="/developer" className="mb-4 inline-flex min-h-[36px] items-center gap-1.5 text-sm text-muted hover:text-primary">← Back to Developers</Link>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Webhooks</h1>
          <p className="text-sm text-muted">
            {loadState === "ready" || loadState === "empty"
              ? `${endpoints.filter((e) => e.active).length} active endpoint${endpoints.filter((e) => e.active).length === 1 ? "" : "s"}`
              : "Register an HTTPS endpoint to receive event deliveries"}
          </p>
        </div>
        <Button onClick={openAdd} disabled={!canManage}>+ Add Endpoint</Button>
      </div>

      <div className="mb-7">
        <div className="mb-3 text-base font-bold text-ink">Endpoints</div>

        {loadState === "loading" && (
          <div role="status" className="rounded-xl border border-line bg-surface px-5 py-6 text-sm text-muted">Loading endpoints…</div>
        )}

        {loadState === "error" && (
          <div className="rounded-xl border border-danger bg-danger-soft px-5 py-4 text-sm text-danger">
            Failed to load webhooks: {loadError}
          </div>
        )}

        {loadState === "empty" && (
          <div className="rounded-xl border border-line bg-surface px-5 py-6 text-sm text-muted">
            No webhook endpoints registered yet. Add one to start receiving event deliveries.
          </div>
        )}

        {loadState === "ready" && (
          <div className="overflow-hidden rounded-xl border border-line bg-surface">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    <th className="px-5 py-2.5">URL</th>
                    <th className="px-3 py-2.5">Events</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Signing secret</th>
                    <th className="px-5 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {endpoints.map((ep) => (
                    <tr key={ep.id} className={!ep.active ? "opacity-65" : ""}>
                      <td className="px-5 py-2.5 font-mono text-xs text-ink">{ep.url}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-muted">{ep.eventTypes.join(", ")}</td>
                      <td className="px-3 py-2.5">
                        <Badge variant={ep.active ? "success" : "neutral"} size="sm">{ep.active ? "Active" : "Deleted"}</Badge>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-muted" title="Alloy never returns the raw signing secret.">
                        {formatSecretRef(ep.secretRef)}
                      </td>
                      <td className="px-5 py-2.5">
                        {/* Three row actions wrap rather than widening the
                            cell, and each gets the 36px tap-target floor a
                            bare `text-xs` button does not meet. */}
                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                          <button
                            type="button"
                            className="inline-flex min-h-[36px] items-center text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => sendTest(ep)}
                            disabled={!canManage || testingId === ep.id}
                          >
                            {testingId === ep.id ? "Sending…" : "Send test"}
                          </button>
                          <button
                            type="button"
                            className="inline-flex min-h-[36px] items-center text-xs font-medium text-muted hover:underline"
                            onClick={() => loadDeliveries(ep.id)}
                          >
                            Deliveries
                          </button>
                          <button
                            type="button"
                            className="inline-flex min-h-[36px] items-center text-xs font-medium text-danger hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => setDeleteTarget(ep)}
                            disabled={!canManage}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {selectedForDeliveries && (
        <div>
          {/* The heading carries an endpoint id, which has no break
              opportunities; the row wraps and the id breaks so the Close
              control is never pushed past a 375px viewport. */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 break-all text-base font-bold text-ink">Recent Deliveries — {selectedForDeliveries}</div>
            <button
              type="button"
              className="inline-flex min-h-[36px] items-center text-xs font-medium text-muted hover:underline"
              onClick={() => setSelectedForDeliveries(null)}
            >
              Close
            </button>
          </div>
          <div className="mb-6 overflow-hidden rounded-xl border border-line bg-surface">
            {deliveriesLoading && (
              <div role="status" className="px-5 py-6 text-sm text-muted">Loading deliveries…</div>
            )}
            {!deliveriesLoading && deliveriesError && (
              <div className="px-5 py-4 text-sm text-danger">Failed to load deliveries: {deliveriesError}</div>
            )}
            {!deliveriesLoading && !deliveriesError && deliveries.length === 0 && (
              <div className="px-5 py-6 text-sm text-muted">No deliveries yet. Send a test delivery to see one here.</div>
            )}
            {!deliveriesLoading && !deliveriesError && deliveries.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                      <th className="px-5 py-2.5">Event</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-3 py-2.5">Attempts</th>
                      <th className="px-3 py-2.5">Last response</th>
                      <th className="px-3 py-2.5">Reason</th>
                      <th className="px-5 py-2.5">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {deliveries.map((d) => (
                      <tr key={d.id}>
                        <td className="px-5 py-2.5 font-mono text-xs text-ink">{d.eventType}</td>
                        <td className="px-3 py-2.5"><DeliveryStatusBadge status={d.status} /></td>
                        <td className="px-3 py-2.5 text-muted">{d.attempts}</td>
                        <td className="px-3 py-2.5 text-muted">{d.lastResponseStatus ?? "—"}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-muted">{d.reasonCode ?? "—"}</td>
                        <td className="px-5 py-2.5 text-muted">{d.createdAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Webhook Endpoint"
        description="Alloy will POST JSON payloads to this URL when the selected events occur."
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={saveEndpoint} disabled={!canManage} loading={saving}>Add Endpoint</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted" htmlFor="wh-url">Endpoint URL</label>
            <input
              id="wh-url"
              type="url"
              placeholder="https://your-server.com/webhook"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
            />
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Events to Subscribe</div>
            {/* One event per line on a phone, two from sm: two columns of
                `diagnostics.timeline_available` are ~420px wide, past the
                dialog's inner width at 375px. The long mono identifiers
                (`min-w-0 break-all`) shrink inside the row rather than
                setting its minimum width. */}
            <div className="grid max-h-[200px] grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
              {WEBHOOK_EVENT_TYPES.map((e) => (
                <label key={e} className="flex min-w-0 items-center gap-2 rounded-md border border-line px-2 py-1.5 font-mono text-[11px] text-ink">
                  <Checkbox checked={!!events[e]} onCheckedChange={(v) => setEvents((prev) => ({ ...prev, [e]: v }))} ariaLabel={e} />
                  <span className="min-w-0 break-all">{e}</span>
                </label>
              ))}
            </div>
          </div>
          <p className="rounded-md border border-line bg-bg px-3 py-2 text-xs text-muted">
            Alloy generates and manages this endpoint's signing secret. It is never displayed — the
            API only ever returns a redacted reference (for example <code>secret-manager://configured</code>).
          </p>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this endpoint?"
        description="This permanently deactivates the endpoint. There is no undo or disable-and-keep option."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={!canManage} loading={deleting}>Delete Endpoint</Button>
          </>
        }
      >
        <p className="text-sm text-muted">This action cannot be undone.</p>
      </Modal>
    </div>
  );
}
